import React, { useState, createContext, useContext, ReactNode, ElementType, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';

interface DropdownContextType {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleOpen: () => void;
}

const DropDownContext = createContext<DropdownContextType | undefined>(undefined);

interface DropdownProps {
  children?: ReactNode;
  as?: ElementType;
  className?: string;
}

const Dropdown = ({ as: Component = 'div', children, className }: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => {
    setOpen((previousState) => !previousState);
  }, []); // Empty dependency array

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (open && toggleOpen) {
          toggleOpen();
        }
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [open, toggleOpen]); // Include toggleOpen as a dependency

  return (
    <DropDownContext.Provider value={{ open, setOpen, toggleOpen }}>
      <Component
        ref={dropdownRef}
        className={`dropdown ${className}`}
      >
        {children}
      </Component>
    </DropDownContext.Provider>
  );
};

interface TriggerProps {
  children: ReactNode;
  type?: ElementType;
  className?: string;
  id?: string;
  href?: any;
}

export const Trigger: React.FC<TriggerProps> = ({ type, children, className, id }) => {
  const { open, toggleOpen } = useContext(DropDownContext)!;

  const getClassNameButton = className ? className : "bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded";
  const getClassNameLink = className ? className : "transition-all duration-200 ease-linear bg-white border-dashed dropdown-toggle text-custom-500 btn border-custom-500 hover:text-custom-500 hover:bg-custom-50 hover:border-custom-600 focus:text-custom-600 focus:bg-custom-50 focus:border-custom-600 active:text-custom-600 active:bg-custom-50 active:border-custom-600 dark:focus:ring-custom-400/20 dark:bg-custom-400/20 ";
  return (
    <>
      {type === 'a' ? (
        <Link id={id} to="/#" onClick={(e: any) => {
          e.preventDefault();
          toggleOpen();
        }} className={getClassNameLink + (open ? " show" : "")}>
          {children}
        </Link>
      ) : (
        <button id={id} onClick={toggleOpen} className={getClassNameButton}>
          {children}
        </button>
      )}
    </>
  );
};


interface ContentProps {
  as?: ElementType;
  align?: 'left' | 'right';
  className?: string;
  width?: string;
  contentClasses?: string;
  children: ReactNode;
  placement?: 'right-end' | 'start-end' | 'top-end' | 'bottom-start' | 'bottom-end' | 'top-start';
  /** موقعیت‌دهی ثابت داخل viewport؛ برای منوهای عریض مثل نوتیفیکیشن موبایل. */
  fitViewport?: boolean;
  /** شناسه‌ی دکمه‌ای که منو باید نسبت به آن تراز شود. */
  anchorId?: string;
}

const Content: React.FC<ContentProps> = ({ as: Component = 'div', className, children, placement, fitViewport = false, anchorId }) => {
  const { open, setOpen } = useContext(DropDownContext)!;

  const getClassName = className || "absolute z-50 py-2 mt-1 text-left list-none bg-white rounded-md shadow-md border border-slate-300 dropdown-menu min-w-max dark:bg-zink-400 dark:border-slate-500";

  const [placementState, setPlacement] = useState('right-end' as 'right-end' | 'start-end' | 'top-end' | 'bottom-start' | 'bottom-end' | 'top-start');

  useEffect(() => {
    if (placement) setPlacement(placement);
  }, [placement]); // Add dependency array to useEffect

  const dropdownElementRef = useRef<HTMLDivElement>(null);

  // موقعیت‌دهی در یک افکت (بعد از commit شدن DOM) انجام می‌شود، نه در حین render،
  // چون در حین render هنوز ref به عنصر واقعی متصل نشده (خصوصاً چون Transition عنصر را
  // هنگام بسته بودن از DOM حذف می‌کند) و موقعیت‌دهی قبلی گاهی اصلاً اعمال نمی‌شد.
  useEffect(() => {
    const dropdownElement = dropdownElementRef.current;
    if (!open || !dropdownElement) return;

    if (fitViewport) {
      const updatePosition = () => {
        const trigger = anchorId
          ? document.getElementById(anchorId)
          : dropdownElement.parentElement?.querySelector<HTMLElement>('.dropdown-toggle');
        if (!trigger) return;

        const triggerRect = trigger.getBoundingClientRect();
        const availableWidth = Math.max(0, window.innerWidth - 16);
        dropdownElement.style.position = 'fixed';
        dropdownElement.style.margin = '0px';
        dropdownElement.style.minWidth = '0px';
        dropdownElement.style.width = `${Math.min(dropdownElement.scrollWidth, availableWidth)}px`;
        dropdownElement.style.maxWidth = `${availableWidth}px`;
        dropdownElement.style.maxHeight = `${Math.max(0, window.innerHeight - 16)}px`;
        dropdownElement.style.overflowY = 'auto';
        dropdownElement.style.transform = 'none';

        const menuRect = dropdownElement.getBoundingClientRect();
        const left = Math.max(8, Math.min(triggerRect.right - menuRect.width, window.innerWidth - menuRect.width - 8));
        const top = Math.max(8, Math.min(triggerRect.bottom + 8, window.innerHeight - menuRect.height - 8));
        dropdownElement.style.left = `${left}px`;
        dropdownElement.style.right = 'auto';
        dropdownElement.style.top = `${top}px`;
        dropdownElement.style.bottom = 'auto';
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }

    dropdownElement.style.position = 'absolute';
    dropdownElement.style.margin = '0px';

    switch (placementState) {
      case 'right-end':
        // این قالب فقط راست‌به‌چپ است؛ دکمه‌های trigger همیشه سمت چپ هدر قرار می‌گیرند
        // پس منو باید از لبه‌ی چپ دکمه به سمت راست (داخل صفحه) باز شود
        dropdownElement.style.inset = '0px auto auto 0px';
        dropdownElement.style.transform = 'translate(0px, 54px)';
        break;
      case 'start-end':
        dropdownElement.style.inset = '0px auto auto 0px';
        dropdownElement.style.transform = 'translate(0px, 20px)';
        break;
      case 'top-end':
        dropdownElement.style.inset = 'auto 0px 0px auto';
        dropdownElement.style.transform = 'translate(-58px, -30px)';
        break;
      case 'bottom-start':
        dropdownElement.style.inset = '0px 0px auto auto';
        dropdownElement.style.transform = 'translate(0px, 54px)';
        break;
      case 'bottom-end':
        dropdownElement.style.inset = '0px 0px auto auto';
        dropdownElement.style.transform = 'translate(0px, 39px)';
        break;
      case 'top-start':
        dropdownElement.style.inset = 'auto auto 0px 0px';
        dropdownElement.style.transform = 'translate(0px, -95px)';
        break;
    }
  }, [open, placementState, fitViewport, anchorId]);

  const content = (
    <Transition
      as={React.Fragment}
      show={open}
      enter="transition ease-out duration-500"
      enterFrom="opacity-0 scale-95 -translate-y-1"
      enterTo="opacity-100 scale-100 translate-y-0"
      leave="transition ease-in duration-200"
      leaveFrom="opacity-100 scale-100 translate-y-0"
      leaveTo="opacity-0 scale-95 -translate-y-1"
    >
      <Component
        ref={dropdownElementRef}
        onClick={fitViewport ? undefined : () => setOpen(false)}
        className={`${getClassName} origin-top`}
      >
        {children}
      </Component>
    </Transition>
  );

  return fitViewport && typeof document !== 'undefined'
    ? createPortal(content, document.body)
    : content;
};


Dropdown.Trigger = Trigger;
Dropdown.Content = Content;


export { Dropdown };
